import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Chip,
    Stack,
    IconButton,
    Tabs,
    Tab,
    CircularProgress,
    useTheme,
    alpha,
    Tooltip,
    Card,
    CardContent
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    CheckCircle as CheckIcon,
    Work as WorkIcon,
    Description as DescriptionIcon,
    DoneAll as DoneAllIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs, EmptyState, useToast } from '../../components/ui';
import { apiGet, apiPut, apiDelete } from '../../lib/apiClient';

interface Notification {
    id: string;
    type: string;
    title: string;
    message: string;
    link?: string;
    is_read: boolean;
    created_at: string;
    metadata?: Record<string, any>;
}

export const NotificationsPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const filter = tabValue === 0 ? '' : tabValue === 1 ? '&unread=true' : '&read=true';
            const data = await apiGet(`/api/notifications?limit=50${filter}`);
            if (data.success) {
                setNotifications(data.notifications || []);
                setTotalCount(data.totalCount || data.notifications?.length || 0);
                setUnreadCount(data.unreadCount || 0);
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
            showToast('Failed to load notifications', 'error');
        } finally {
            setLoading(false);
        }
    }, [tabValue, showToast]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleNotificationClick = async (notification: Notification) => {
        // Mark as read if unread
        if (!notification.is_read) {
            try {
                await apiPut('/api/notifications', { notification_id: notification.id });
                setNotifications(prev =>
                    prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (err) {
                console.error('Error marking notification as read:', err);
            }
        }

        // Navigate if link exists
        if (notification.link) {
            navigate(notification.link);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            setActionLoading(true);
            await apiPut('/api/notifications', { mark_all: true });
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
            showToast('All notifications marked as read', 'success');
        } catch (err) {
            console.error('Error marking all as read:', err);
            showToast('Failed to mark all as read', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteNotification = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await apiDelete(`/api/notifications?id=${id}`);
            setNotifications(prev => prev.filter(n => n.id !== id));
            setTotalCount(prev => prev - 1);
            showToast('Notification deleted', 'success');
        } catch (err) {
            console.error('Error deleting notification:', err);
            showToast('Failed to delete notification', 'error');
        }
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'job_pending_approval':
                return <WorkIcon sx={{ color: theme.palette.warning.main }} />;
            case 'job_approved':
                return <CheckIcon sx={{ color: theme.palette.success.main }} />;
            case 'application_received':
                return <DescriptionIcon sx={{ color: theme.palette.primary.main }} />;
            default:
                return <NotificationsIcon sx={{ color: theme.palette.grey[500] }} />;
        }
    };

    const getNotificationTypeLabel = (type: string) => {
        switch (type) {
            case 'job_pending_approval':
                return { label: 'Pending Approval', color: 'warning' as const };
            case 'job_approved':
                return { label: 'Approved', color: 'success' as const };
            case 'application_received':
                return { label: 'Application', color: 'primary' as const };
            default:
                return { label: 'Notification', color: 'default' as const };
        }
    };

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
        if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
        if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredNotifications = notifications;

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                items={[
                    { label: 'Dashboard', href: '/admin/dashboard' },
                    { label: 'Notifications' }
                ]}
            />

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            Notifications
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {unreadCount > 0
                                ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                                : 'All caught up!'}
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchNotifications} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    {unreadCount > 0 && (
                        <Button
                            variant="outlined"
                            startIcon={actionLoading ? <CircularProgress size={16} /> : <DoneAllIcon />}
                            onClick={handleMarkAllRead}
                            disabled={actionLoading}
                        >
                            Mark All Read
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>All</span>
                                <Chip label={totalCount} size="small" />
                            </Stack>
                        }
                    />
                    <Tab
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Unread</span>
                                {unreadCount > 0 && <Chip label={unreadCount} size="small" color="primary" />}
                            </Stack>
                        }
                    />
                    <Tab label="Read" />
                </Tabs>
            </Paper>

            {/* Notifications List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : filteredNotifications.length === 0 ? (
                <EmptyState
                    icon={<NotificationsIcon sx={{ fontSize: 64 }} />}
                    title={tabValue === 1 ? "No unread notifications" : "No notifications"}
                    description={tabValue === 1
                        ? "You're all caught up! Check back later for new updates."
                        : "You don't have any notifications yet. They'll appear here when something happens."}
                />
            ) : (
                <Stack spacing={2}>
                    {filteredNotifications.map((notification) => {
                        const typeInfo = getNotificationTypeLabel(notification.type);
                        return (
                            <Card
                                key={notification.id}
                                sx={{
                                    cursor: notification.link ? 'pointer' : 'default',
                                    transition: 'all 0.2s',
                                    backgroundColor: notification.is_read
                                        ? 'background.paper'
                                        : alpha(theme.palette.primary.main, 0.04),
                                    borderLeft: notification.is_read
                                        ? 'none'
                                        : `4px solid ${theme.palette.primary.main}`,
                                    '&:hover': {
                                        boxShadow: notification.link ? theme.shadows[4] : theme.shadows[1],
                                        backgroundColor: notification.is_read
                                            ? alpha(theme.palette.action.hover, 0.04)
                                            : alpha(theme.palette.primary.main, 0.08),
                                    }
                                }}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <CardContent sx={{ py: 2 }}>
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        {/* Icon */}
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '50%',
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            {getNotificationIcon(notification.type)}
                                        </Box>

                                        {/* Content */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                <Box>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight={notification.is_read ? 500 : 700}
                                                        >
                                                            {notification.title}
                                                        </Typography>
                                                        <Chip
                                                            label={typeInfo.label}
                                                            size="small"
                                                            color={typeInfo.color}
                                                            variant="outlined"
                                                        />
                                                        {!notification.is_read && (
                                                            <Chip
                                                                label="New"
                                                                size="small"
                                                                color="primary"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}
                                                    </Stack>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mb: 1 }}
                                                    >
                                                        {notification.message}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.disabled">
                                                        {formatDateTime(notification.created_at)}
                                                    </Typography>
                                                </Box>

                                                {/* Actions */}
                                                <Tooltip title="Delete notification">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleDeleteNotification(notification.id, e)}
                                                        sx={{
                                                            opacity: 0.5,
                                                            '&:hover': { opacity: 1, color: 'error.main' }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
            )}
        </Container>
    );
};

export default NotificationsPage;
